import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Paper, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import moment from "moment";
import * as React from "react";
import classNames from "../../../helpers/classNames";
import { BillItemType } from "../../../interfaces/BillInterfaces";
import { Stock } from "../../../interfaces/StockInterfaces";
import styles from "./BillEditor.module.css";

interface Props {
  showBatchPopup: boolean;
  setShowBatchPopup: () => void;
  batchOptions: Stock[];
  quantityRef: HTMLElement;
  handleSwitchBatch: () => void;
  item: BillItemType;
  updateBillItem: (billItem: BillItemType) => void;
  infoText: string;
  setInfoText: (value: string) => void;
}

const BatchInfoPopup = ({
  showBatchPopup,
  setShowBatchPopup,
  batchOptions,
  quantityRef,
  handleSwitchBatch,
  item,
  infoText,
  setInfoText,
  updateBillItem
}: Props): React.JSX.Element | null => {
  const popupRef = React.useRef(null);

  const handleClickOutside = (event) => {
    if (
      !popupRef?.current?.contains(event.target) &&
      !quantityRef?.current?.contains(event.target)
    ) {
      setShowBatchPopup(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!showBatchPopup || !batchOptions?.length) return null;

  const batchesQuantityTotal = batchOptions.reduce(
    (total, curr) => total + Number(curr.quantity),
    0
  );
  const unselectedBatchIds = batchOptions
    .filter((batchOpt) => batchOpt.batchId !== item.batchInfo.batchId)
    .map((batch) => batch.batchId);

  return (
    <Paper
      sx={{
        width: "560px",
        position: "absolute",
        zIndex: 5,
        transform: "translate(-50%, -110%)",
        border: "1px solid #e0e0e0"
      }}
      ref={popupRef}
    >
      <Grid
        container
        spacing={1}
        size={12}
        sx={{
          padding: "8px",
          maxHeight: "200px",
          overflow: "auto",
          marginBottom: "4px"
        }}
      >
        <Grid size={3}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, paddingLeft: "12px" }}>
            Batch Id
          </Typography>
        </Grid>
        <Grid size={3}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Expiry Date</Typography>
        </Grid>
        <Grid size={3}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Available Qty</Typography>
        </Grid>
        <Grid size={3}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Rack</Typography>
        </Grid>
      </Grid>

      {batchOptions.map((batchOption) => {
        const isExpired = moment().isAfter(batchOption.expiryDate);
        const canSwitchBatch = unselectedBatchIds.includes(batchOption.batchId);
        const isSelectedBatch = batchOption.batchId === item.batchInfo.batchId;

        return (
          <Grid
            container
            size={12}
            key={batchOption.batchId}
            spacing={1}
            className={classNames({ [styles.batchItemHover]: canSwitchBatch })}
            onClick={() => {
              if (!isExpired && canSwitchBatch) {
                handleSwitchBatch(batchOption);
              }
            }}
          >
            <Grid size={3}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CheckIcon
                  color="primary"
                  style={{
                    fontSize: 14,
                    width: "20px",
                    color: !isSelectedBatch ? "transparent" : ""
                  }}
                />
                <Typography style={{ fontSize: "12px" }}>{batchOption.batchId}</Typography>
              </Box>
            </Grid>
            <Grid size={3}>
              <Typography
                style={{
                  fontSize: "12px",
                  color: `${isExpired ? "red" : ""}`
                }}
              >
                {batchOption.expiryDate.slice(0, 10)}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography style={{ fontSize: "12px" }}>
                {Math.trunc(Number(batchOption.quantity))}{" "}
                {batchOption.product.unitsPerPackage > 1 &&
                  `(${Math.floor(
                    Number(batchOption.quantity) / batchOption.product.unitsPerPackage
                  )} packages)`}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography
                style={{
                  fontSize: "12px",
                  color: `${isExpired ? "red" : ""}`
                }}
              >
                {batchOption?.product?.rack || "-"}
              </Typography>
            </Grid>
          </Grid>
        );
      })}

      <Grid
        size={12}
        container
        sx={{ marginTop: "4px", padding: "4px", borderTop: "1px solid #e0e0e0" }}
        spacing={1}
      >
        <Grid size={8} alignItems="center">
          <Typography sx={{ fontSize: "12px", textAlign: "right" }}>Total Quantity</Typography>
          {batchOptions[0]?.product?.structuredInfo?.isNarcotics && (
            <Typography sx={{ fontSize: "12px", textAlign: "right" }}>Narcotic</Typography>
          )}
        </Grid>
        <Grid size={4} alignItems="center">
          <Typography sx={{ fontSize: "12px", fontWeight: 600, textAlign: "center" }}>
            {batchesQuantityTotal}
          </Typography>

          {batchOptions[0]?.product?.structuredInfo?.isNarcotics && (
            <Box sx={{ textAlign: "center" }}>
              <WarningIcon sx={{ color: "red" }} />
            </Box>
          )}
        </Grid>
      </Grid>

      {batchOptions[0]?.product?.structuredInfo?.isNarcotics && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1,
            mx: 1
          }}
        >
          <Typography component="label">Info</Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Add information"
            fullWidth
            value={infoText}
            onChange={({ target }) => setInfoText(target.value)}
            onBlur={() =>
              updateBillItem({
                ...item,
                info: infoText
              })
            }
          />
        </Box>
      )}
    </Paper>
  );
};

export default BatchInfoPopup;
